<template>
  <div class="voice-intelligence-dashboard">
    <div class="row border-bottom white-bg dashboard-header">
      <div class="col-md-6 header-title">
        <h2>
          <span class="fa fa-microphone"></span>
          Voice Intelligence
        </h2>
      </div>

      <div class="header-item" v-if="isShowFilter">
        <span class="fa fa-fw fa-clock-o"></span>
        <strong>View by Show |</strong>

        <el-select
          size="small"
          v-model="selectedTimeslot"
          placeholder="Select slot"
          :loading="loadingTimeslots"
          @change="onTimeslotChanged"
        >
          <el-option
            v-for="slot in timeslots"
            :key="slot.id"
            :label="slot.title"
            :value="slot.id"
          />
        </el-select>
      </div>
    </div>

    <div class="wrapper wrapper-content animated fadeInRight">
      <div class="row">
        <div
          class="col-md-3"
          v-for="block in analyticsBlocks"
          :key="block.unit"
          @click="selectBlock(block)"
        >
          <voice-analytics-unit-block
            :unit="block.unit"
            :unit-class="block.unitClass"
            :period="block.period"
            :description="block.description"
            :received="block.received"
            :growth="block.growth"
            :stats="block.stats"
            :approved="block.approved"
            :flagged="block.flagged"
            :pending="block.pending"
            :selected="block.selected"
            @previous="onBlockPrevious(block)"
            @next="onBlockNext(block)"
          />
        </div>

        <div class="col-lg-3">
          <filters-sidebar
            ref="filtersSidebar"
            :transcripts="transcripts"
            :tone-options="getToneOptions"
            :tag-options="getTagOptions"
            @apply-filters="filters = $event"
            @reset-filters="clearFilters"
          />
        </div>

        <div class="col-lg-9">
          <div class="ibox">
            <div class="ibox-content">
              <el-row type="flex" align="middle" justify="space-between" :gutter="10">
                <el-col :flex="1">
                  <el-input
                    v-model="searchQuery"
                    placeholder="Search by name, transcript, tags..."
                    prefix-icon="el-icon-search"
                    clearable
                    @clear="searchQuery = ''"
                    size="small"
                  />
                </el-col>
                <el-col :flex="0">
                  <div class="pull-right">
                    <el-button-group size="small">
                      <el-button
                        :disabled="viewMode !== 'table'"
                        @click="onExportSelected"
                        size="small"
                      >
                        <i class="fa fa-download"></i> Export
                      </el-button>
                      <el-button
                        :disabled="viewMode !== 'table'"
                        @click="onBulkApprove"
                        size="small"
                      >
                        <i class="fa fa-check"></i> Approve Selected
                      </el-button>
                      <el-button @click="toggleViewMode" size="small">
                        <i :class="viewMode === 'table' ? 'fa fa-th-large' : 'fa fa-list'"></i>
                        {{ viewMode === 'table' ? 'Grid' : 'Table' }}
                      </el-button>
                    </el-button-group>
                  </div>
                </el-col>
              </el-row>
            </div>
          </div>

          <div class="ibox">
            <div class="ibox-content">
              <voice-notes-table
                ref="voiceNotesTable"
                v-if="viewMode === 'table'"
                :transcripts="getTranscripts"
                :loadingTranscripts="loadingTranscripts"
                @selection-change="handleSelectionChange"
                @preview-audio="previewVoiceNote"
                @approve-on-air="onApproveOnAir"
                @toggle-flag="onToggleFlag"
                @refresh-transcripts="onRefreshTranscripts"
              />
              <voice-notes-grid
                v-else
                :transcripts="getTranscripts"
                :loadingTranscripts="loadingTranscripts"
                @preview-audio="previewVoiceNote"
                @approve-on-air="onApproveOnAir"
                @toggle-flag="onToggleFlag"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <audio-preview-modal :visible.sync="previewVisible" :note="previewNote" @close="closePreview" />

    <approve-modal
      :visible.sync="approveVisible"
      :note="approveNote"
      :loading="approvingLoading"
      @approve="handleApproveSubmit"
      @close="closeApprove"
    />

    <flag-modal
      :visible.sync="flagVisible"
      :note="flagNote"
      :loading="flaggingLoading"
      @flag="handleFlagSubmit"
      @close="closeFlag"
    />

    <bulk-approve-modal
      :visible.sync="bulkApproveVisible"
      :notes="selectedRows"
      :loading="bulkApprovingLoading"
      @bulk-approve="handleBulkApproveSubmit"
      @close="closeBulkApprove"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import VoiceNotesTable from '@/components/analytics/voiceIntelligence/VoiceNotesTable';
import VoiceNotesGrid from '@/components/analytics/voiceIntelligence/VoiceNotesGrid';
import VoiceAnalyticsUnitBlock from '@/components/analytics/voiceIntelligence/VoiceAnalyticsUnitBlock';
import AudioPreviewModal from '@/components/analytics/voiceIntelligence/modals/AudioPreviewModal';
import ApproveModal from '@/components/analytics/voiceIntelligence/modals/ApproveModal';
import BulkApproveModal from '@/components/analytics/voiceIntelligence/modals/BulkApproveModal';
import FlagModal from '@/components/analytics/voiceIntelligence/modals/FlagModal';
import FiltersSidebar from '@/components/analytics/voiceIntelligence/FiltersSidebar';

export default {
  name: 'VoiceIntelligenceDashboard',
  components: {
    VoiceNotesTable,
    VoiceNotesGrid,
    VoiceAnalyticsUnitBlock,
    AudioPreviewModal,
    ApproveModal,
    BulkApproveModal,
    FlagModal,
    FiltersSidebar,
  },
  props: {
    transcripts: { type: Array, required: true },
    timeslots: { type: Array, required: true },
    loadingTranscripts: { type: Boolean, default: false },
    loadingTimeslots: { type: Boolean, default: false },
    loadingAnalytics: { type: Boolean, default: false },
    transcriptAnalytics: { type: Object, default: null },
  },
  data() {
    return {
      detailVisible: false,
      filters: {
        minScore: 0,
        selectedTones: [],
        selectedTags: [],
        maxLength: 120,
      },

      previewVisible: false,
      previewNote: null,

      approveVisible: false,
      approveNote: null,
      approvingLoading: false,

      flagVisible: false,
      flagNote: null,
      flaggingLoading: false,

      bulkApproveVisible: false,
      bulkApprovingLoading: false,

      selectedNote: null,
      searchQuery: '',
      selectedFilter: 'Show',
      selectedRows: [],
      selectedTimeslot: '0',
      viewMode: 'table',
      selectedBlockUnit: 'Year',
    };
  },
  methods: {
    closePreview() {
      this.previewVisible = false;
      this.previewNote = null;
    },

    previewVoiceNote(transcript) {
      this.previewNote = transcript;
      this.previewVisible = true;
    },

    onApproveOnAir(transcript) {
      this.approveNote = transcript;
      this.approveVisible = true;
    },

    onBulkApprove() {
      if (this.selectedRows.length === 0) {
        this.$notify.warning('Please select voice notes to approve');
        return;
      }
      this.bulkApproveVisible = true;
    },

    handleApproveSubmit(payload) {
      this.approvingLoading = true;
      this.$emit('approve-query', payload);
      this.approvingLoading = false;
      this.closeApprove();
    },

    closeApprove() {
      this.approveVisible = false;
      this.approveNote = null;
      this.approvingLoading = false;
    },

    onToggleFlag(transcript) {
      this.flagNote = transcript;
      this.flagVisible = true;
    },

    handleFlagSubmit(payload) {
      this.flaggingLoading = true;
      this.$emit('flag-query', {
        id: payload.id,
        flagStatus: payload.flagStatus,
        action: payload.flagStatus ? 'flagged' : 'unflagged',
        flaggedById: payload.flaggedById,
        flaggedBy: payload.flaggedBy,
        reason: payload.reason,
      });
      this.flaggingLoading = false;
      this.closeFlag();
    },

    handleBulkApproveSubmit(payload) {
      this.bulkApprovingLoading = true;
      this.$emit(
        'bulk-approve-query',
        payload.notes.map(note => ({
          interactionId: note,
          approvedById: payload.approvedById,
          approvedBy: payload.approvedBy,
          approveStatus: payload.approveStatus,
          approveReason: payload.reason,
        })),
      );
      this.bulkApprovingLoading = false;
      this.closeBulkApprove();
    },

    closeBulkApprove() {
      this.bulkApproveVisible = false;
      this.bulkApprovingLoading = false;
      this.selectedRows = [];
      this.clearSelection();
    },

    closeFlag() {
      this.flagVisible = false;
      this.flagNote = null;
      this.flaggingLoading = false;
    },

    clearSelection() {
      if (this.$refs.voiceNotesTable) {
        this.$refs.voiceNotesTable.onClearSelection();
      }
    },
    clearFilters() {
      this.searchQuery = '';
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },
    openDetail(transcript) {
      this.selectedNote = { ...transcript };
      this.detailVisible = true;
    },
    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'grid' : 'table';
    },
    getFilteredTranscripts(transcripts) {
      let filtered = [...transcripts];

      if (this.searchQuery) {
        const searchQuery = this.searchQuery.toLowerCase();

        filtered = filtered.filter(transcript => {
          const sender = transcript.sender ? transcript.sender.toLowerCase() : '';
          const body = transcript.transcript ? transcript.transcript.toLowerCase() : '';
          const tone = transcript.tone ? transcript.tone.toLowerCase() : '';
          const location = transcript.location ? transcript.location.toLowerCase() : '';
          const tags = transcript.tags || [];

          return (
            sender.includes(searchQuery) ||
            tags.some(tag => (tag ? tag.toLowerCase().includes(searchQuery) : false)) ||
            body.includes(searchQuery) ||
            tone.includes(searchQuery) ||
            location.includes(searchQuery)
          );
        });
      }

      if (this.filters.minScore > 0) {
        filtered = filtered.filter(transcript => transcript.score >= this.filters.minScore);
      }
      if (this.filters.selectedTones.length > 0) {
        filtered = filtered.filter(transcript =>
          this.filters.selectedTones.includes(transcript.tone),
        );
      }
      if (this.filters.selectedTags.length > 0) {
        filtered = filtered.filter(transcript =>
          this.filters.selectedTags.some(tag => transcript.tags.includes(tag)),
        );
      }
      if (this.filters.maxLength < 120) {
        filtered = filtered.filter(note => note.audioDuration <= this.filters.maxLength);
      }
      if (this.$refs.filtersSidebar) {
        this.$refs.filtersSidebar.quickFilters.forEach(filter => {
          if (filter.active) {
            filtered = filtered.filter(filter.filter);
          }
        });
      }
      return filtered;
    },
    onTimeslotChanged(timeslotId) {
      this.selectedTimeslot = timeslotId;
      this.$emit('timeslot-query', timeslotId);
    },
    onRefreshTranscripts() {
      if (this.selectedTimeslot === '0') {
        this.$notify.warning('Please select a timeslot');
        return;
      }
      this.$emit('refresh-transcripts');
    },
    onExportSelected() {
      if (!this.selectedRows.length) {
        this.$notify.warning('Please select voice notes to export');
        return;
      }

      this.$confirm(`Export ${this.selectedRows.length} selected voice notes?`, 'Export', {
        confirmButtonText: 'Export',
        cancelButtonText: 'Cancel',
      })
        .then(() => {
          this.$emit('export-selected-query', this.selectedRows);
          this.selectedRows = [];
          this.clearSelection();
        })
        .catch(() => {
          this.selectedRows = [];
          this.clearSelection();
        });
    },
    onBlockPrevious(block) {
      this.$emit('period-shift', { unit: block.unit, direction: 'previous' });
    },
    onBlockNext(block) {
      this.$emit('period-shift', { unit: block.unit, direction: 'next' });
    },
    selectBlock(selectedBlock) {
      this.selectedBlockUnit = selectedBlock.unit;
    },
  },
  computed: {
    ...mapState('user', ['me']),
    analyticsBlocks() {
      const defaults = {
        period: '',
        received: 0,
        growth: 0,
        stats: [],
        approved: 0,
        flagged: 0,
        pending: 0,
      };
      const toBlock = data => Object.assign({}, defaults, data);

      const a = this.transcriptAnalytics;
      const day = toBlock(a && a.day);
      const week = toBlock(a && a.week);
      const month = toBlock(a && a.month);
      const year = toBlock(a && a.year);

      return [
        {
          unit: 'Day',
          unitClass: 'label-info',
          description: 'Voice notes received today',
          selected: this.selectedBlockUnit === 'Day',
          ...day,
        },
        {
          unit: 'Week',
          unitClass: 'label-success',
          description: 'Voice notes received this week',
          selected: this.selectedBlockUnit === 'Week',
          ...week,
        },
        {
          unit: 'Month',
          unitClass: 'label-primary',
          description: 'Voice notes received this month',
          selected: this.selectedBlockUnit === 'Month',
          ...month,
        },
        {
          unit: 'Year',
          unitClass: 'label-danger',
          description: 'Voice notes received this year',
          selected: this.selectedBlockUnit === 'Year',
          ...year,
        },
      ];
    },
    isShowFilter() {
      return this.selectedFilter === 'Show';
    },
    getTranscripts() {
      return this.getFilteredTranscripts(this.transcripts);
    },
    getTagOptions() {
      const tagsSet = new Set();
      this.transcripts.forEach(t => {
        if (Array.isArray(t.tags)) t.tags.forEach(tag => tagsSet.add(tag));
      });
      return Array.from(tagsSet).sort();
    },
    getToneOptions() {
      const tonesSet = new Set();
      this.transcripts.forEach(t => {
        if (t.tone) tonesSet.add(t.tone);
      });
      return Array.from(tonesSet).map(tone => ({ label: tone, value: tone }));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/style/_variables_live.scss';

.voice-intelligence-dashboard {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.dashboard-header {
  padding: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  .header-title {
    flex: 1 1 auto;
    margin-left: 1em;

    h2 {
      margin: 0;
      font-size: 24px;

      .fa {
        color: $colony-purple;
        margin-right: 10px;
      }
    }
  }

  .header-item {
    flex: 0 0 auto;
    font-size: 1.1em;
    color: $colony-purple;
    max-height: 1.5em;
    margin-right: 2em;
    display: flex;
    align-items: center;
    gap: 0.5em;

    .fa {
      font-size: 1.3em;
    }

    strong {
      font-weight: 600;
    }
  }
}

.wrapper-content {
  padding: 20px;
}

.ibox {
  background: #fff;
  border: 1px solid #e7eaec;
  border-radius: 5px;
  margin-bottom: 20px;

  .ibox-content {
    padding: 15px;
  }
}

.row {
  margin-left: -10px;
  margin-right: -10px;

  > [class*='col-'] {
    padding-left: 10px;
    padding-right: 10px;
  }
}
</style>


// WEBPACK FOOTER //
// VoiceIntelligenceDashboard.vue?ab7f5cda